import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Input, notification } from "antd";
import { BookFilled, CloseCircleFilled } from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Translate } from "../../translate";
import { normalizeMongolianPhone } from "../../utils/functions";
import CheckRole from "./CheckRole";
import Loading from "../Loading";
import { getStore } from "../../utils/global";
import axios from "../../utils/axios";
import custom from "../../utils/custom";
import SelectContacts from "./SelectContacts";
import { useMutation, useQuery } from "react-query";

const Step1 = ({ data }, ref) => {
  const ln = typeof getStore("lang") === "object" ? "en" : getStore("lang");
  let { parts, docid } = data;
  const color = useRef(getStore("color"));
  const user = useRef(getStore("user"));
  const roles = useRef(getStore("roles"));
  const [tempParticipants, setTempParticipants] = useState(parts || []);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactIndex, setContactIndex] = useState(0);
  const mainindex = useRef(1);

  const { data: sysConts = [] } = useQuery({
    queryKey: ["get_sys_contacts"],
    queryFn: () =>
      axios.post(
        "client/api/user/contacts",
        {
          page: 0,
          pper: 10,
          search: "",
        },
        { showErrorMessage: true },
      ),
    select: ({ data }) =>
      data?.contacts?.map((e, i) => {
        return { ...e, key: i };
      }) || [],
  });

  useImperativeHandle(ref, () => ({ next: () => handleNext() }));

  const add = () => {
    if (tempParticipants.length < 10) {
      mainindex.current++;
      setTempParticipants((prev) => [
        ...prev,
        {
          key: mainindex.current,
          msisdn: "",
          email: "",
          user_id: 0,
          color: color.current[mainindex.current % color.current.length],
          bg_color: color.current[mainindex.current % color.current.length].replace(", 1)", ", 0.3)"),
          actionrole: roles.current[0].actionrole,
          can_sign: true,
          move_sign: false,
          coords: [],
        },
      ]);
    } else {
      notification.warning({ message: "Max limit is 10!" });
    }
  };

  const remove = (i) => {
    setTempParticipants((prev) => prev.filter((_, index) => index !== i));
  };

  useEffect(() => {
    if (tempParticipants.length === 0) {
      add();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ondrop = (e) => {
    if (!e.destination) {
      return;
    }
    if (e.destination.index === e.source.index) {
      return;
    }
    const newList = Array.from(tempParticipants);
    const [removed] = newList.splice(e.source.index, 1);
    newList.splice(e.destination.index, 0, removed);
    setTempParticipants(newList);
  };

  const handleContact = (i) => {
    setContactOpen(true);
    setContactIndex(i);
  };

  const onSelect = (per) => {
    let active = tempParticipants[contactIndex];
    active.msisdn = per.msisdn;
    active.email = per.email_address;
    active.name = per.name;
    active.user_id = per.id;
    active.disable = true;
    active.kdss_user = per.kdss_user;
    setContactOpen(false);
  };

  const handleNext = async () => {
    if (sysConts.length === 0) {
      notification.warn({ duration: 2, message: "Loading contacts please wait." });
      return { success: false, message: "Loading contacts please wait." };
    }
    if (tempParticipants.some((el) => el.msisdn.length === 0)) {
      notification.warning({
        message: "Please fill the all participants",
        duration: 2,
      });
      return { success: false, message: "Please fill the all participants" };
    }

    const updatedParticipants = tempParticipants.map((el, idx) => ({
      ...el,
      order: idx + 1,
    }));

    updatedParticipants.forEach((el) => {
      sysConts?.forEach((cont) => {
        if (el.msisdn[0] === "3") {
          if (el.msisdn === cont.kdss_msisdn) {
            el.name = cont.kdss_name;
            el.user_id = cont.kdss_user;
            el.kdss_user = cont.kdss_user;
          }
        } else {
          let allx = "976" + normalizeMongolianPhone(el.msisdn);

          if (allx === cont.kdss_msisdn) {
            el.name = cont.kdss_name;
            el.user_id = cont.kdss_user;
            el.kdss_user = cont.kdss_user;
          }
        }
      });
    });
    const { success, message } = await mutateAsync(updatedParticipants);
    if (success) {
      return { success, message, parts: updatedParticipants };
    } else {
      return { success, message };
    }
  };

  const { mutateAsync, isLoading } = useMutation({
    mutationKey: ["add_participants_to_doc"],
    mutationFn: (participants) => axios.post("client/api/doc/signer/add", { docid, recs: participants }, { showErrorMessage: true }),
  });

  return (
    <>
      <Loading loading={isLoading} />
      <div className="step1_container relafull">
        {contactOpen && (
          <SelectContacts
            isOpen={contactOpen}
            onCancel={() => {
              setContactOpen(false);
            }}
            onSelect={onSelect}
          />
        )}
        <div className="step1_detail">
          <div className="btn flex_center" bg_color="5" onClick={add}>
            {Translate[ln].NewDoc.Participants.AddPart}
          </div>
          <div className="step1_user_detail" style={{ marginTop: 20 }}>
            <div className="step1_user_detail_title"> {Translate[ln].NewDoc.Participants.Sender}</div>
            <div className="step1_user_detail_body">
              <div className="user_dropdown_name">{user.current.kdss_name}</div>
              <div className="user_dropdown_mobile">{user.current.kdss_msisdn}</div>
              <div className="user_dropdown_email" color="1">
                {user.current.kdss_email}
              </div>
            </div>
          </div>
          <div className="parts_scroll_area">
            <div className="step1_user_detail" style={{ marginTop: 20 }}>
              <div className="step1_user_detail_title"> {Translate[ln].NewDoc.Participants.FileDetails}</div>
              <div className="step1_user_detail_body" style={{ paddingBottom: 10 }}>
                <div className="user_dropdown_name"> {Translate[ln].NewDoc.Participants.Filename}</div>
                <div className="user_dropdown_mobile">{data.file.name}</div>
                <div className="user_dropdown_name">{Translate[ln].NewDoc.Participants.Filesize}</div>
                <div className="user_dropdown_mobile">{custom.currency(data.file.size / 1000)}KB</div>
              </div>
            </div>
          </div>
        </div>
        <div className="step1_body">
          <div className="absfull">
            <DragDropContext onDragEnd={ondrop}>
              <div className="step_title">
                {Translate[ln].NewDoc.Participants.Title}({tempParticipants.length})
              </div>
              <div className="parts_container">
                <Droppable droppableId="droppable">
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {tempParticipants.map((per, i) => {
                        return (
                          <Draggable key={per.key + "c" + i} draggableId={"drag_" + per.key} index={i}>
                            {(provided) => (
                              <PerPart
                                removable={tempParticipants.length > 1 ? 1 : 0}
                                onChange={(key, val) => {
                                  setTempParticipants((prev) => {
                                    const updated = [...prev];
                                    updated[i][key] = val;
                                    return updated;
                                  });
                                }}
                                remove={() => remove(i)}
                                contact={() => handleContact(i)}
                                provided={provided}
                                per={per}
                              />
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            </DragDropContext>
          </div>
        </div>
      </div>
    </>
  );
};

const PerPart = ({ per, provided, removable, remove, onChange, contact }) => {
  let { innerRef, draggableProps, dragHandleProps } = provided;
  let style = { ...draggableProps.style, ...{ borderLeftColor: per.color } };
  let disabled = per.user_id ? true : false;
  return (
    <div removable={removable} ref={innerRef} {...draggableProps} {...dragHandleProps} style={style} className="per_part" key={per.key} role={per.role}>
      <div className="role_container">
        <CheckRole
          actionrole={per.actionrole}
          onChange={(active) => {
            onChange("actionrole", active.actionrole);
            onChange("can_sign", active.key === 0 ? true : false);
          }}
        />
      </div>
      <div className="input_title">
        Mobile ID&nbsp;<span style={{ color: "red" }}>*</span>
      </div>
      <div className="input_body">
        <Input
          type="text"
          value={per.msisdn}
          onChange={(a) => {
            onChange("msisdn", a.target.value);
          }}
          placeholder="Phone number"
          disabled={disabled}
        />
      </div>
      <div className="input_title">Email</div>
      <div className="input_body">
        <Input
          type="text"
          value={per.email}
          onChange={(a) => {
            onChange("email", a.target.value);
          }}
          placeholder="User email address"
          disabled={disabled}
          suffix={
            <div className="input_suffix flex_center" onClick={contact}>
              <BookFilled />
            </div>
          }
        />
      </div>
      <div className="remove_button" onClick={remove}>
        <div className="flex_center">
          <CloseCircleFilled />
        </div>
      </div>
    </div>
  );
};

export default forwardRef(Step1);
